<template>
  <div class="flex flex-col gap-y-6 relative" @click="closeAllFilters">
    <div
      v-if="flags.enabledExceptionalitySummary"
      class="flex flex-wrap gap-4"
      data-testid="exceptionality-summary-cards"
    >
      <template v-if="summaryLoading">
        <div
          v-for="n in 3"
          :key="`summary-skeleton-${n}`"
          class="rounded-lg border-2 border-gray-200 bg-white p-4 flex flex-col gap-3 flex-1 min-w-[180px] animate-pulse"
        >
          <div class="h-6 bg-slate-200 rounded-full w-28" />
          <div class="h-9 bg-slate-200 rounded w-16" />
        </div>
      </template>
      <template v-else>
        <ExceptionalitySummaryCard
          :status="ExceptionStatusEnum.AWAITING_APPROVAL"
          :count="summaryData.awaitingApproval"
        />
        <ExceptionalitySummaryCard
          :status="ExceptionStatusEnum.APPROVED"
          :count="summaryData.approved"
        />
        <ExceptionalitySummaryCard
          :status="ExceptionStatusEnum.REJECTED"
          :count="summaryData.rejected"
        />
      </template>
    </div>

    <div class="w-full flex flex-col lg:flex-row items-center gap-2">
      <div class="w-full lg:w-1/4">
        <SearchInput
          v-model="textInput"
          @input="onSearchByTabIndex"
          width="100%"
          :loading="loading"
          label="Buscar"
          hide-details
          density="compact"
        />
      </div>

      <div class="flex flex-wrap items-center gap-2 w-full lg:flex-1">
        <div class="relative">
          <div @click.stop="toggleFilter('status')">
            <SelectFilter
              label="Status"
              :icon="statusIcon"
              :options="statusOptions"
              dropdown-width="w-40"
              @select="handleStatusSelect"
              :is-open="openFilterName === 'status'"
              multiple
              :selectedValue="selectedStatusKeys"
            />
          </div>
        </div>

        <div @click.stop="toggleFilter('date')">
          <DateFilter
            ref="dateFilterRef"
            :is-open="openFilterName === 'date'"
            @select="handleDateFilter"
          />
        </div>

        <div class="relative">
          <div @click.stop="toggleFilter('schedule')">
            <SelectFilter
              label="Escala"
              :icon="scaleIcon"
              :options="scheduleOptions"
              searchable
              dropdown-width="w-64"
              @select="handleScheduleSelect"
              :is-open="openFilterName === 'schedule'"
              multiple
              :selectedValue="selectedScheduleIds"
            />
          </div>
        </div>

        <div class="relative">
          <div @click.stop="toggleFilter('unit')">
            <SelectFilter
              label="Unidades"
              :icon="hospitalIcon"
              :options="healthUnitOptions"
              searchable
              dropdown-width="w-64"
              @select="handleHealthUnitSelect"
              :is-open="openFilterName === 'unit'"
              multiple
              :selectedValue="selectedHealthUnitIds"
            />
          </div>
        </div>

        <div class="relative">
          <div @click.stop="toggleFilter('value')">
            <ValueRangeFilter
              ref="valueFilterRef"
              label="Valor"
              :icon="moneyIcon"
              :is-open="openFilterName === 'value'"
              @select="handleValueFilter"
            />
          </div>
        </div>
      </div>

      <div
        v-if="flags.enabledApprovalEngine"
        class="w-full flex justify-end lg:w-auto lg:ml-auto shrink-0"
      >
        <DsSwitch
          v-model="pendingMyActionFilter"
          label="Apenas minhas pendências"
          size="small"
          @update:model-value="handlePendingMyActionToggle"
        />
      </div>
    </div>

    <ActiveFiltersTags
      :filters="unifiedFiltersList"
      :show-clear-button="hasActiveFilters"
      @remove="handleRemoveGenericFilter"
      @clearAll="clearAllFilters"
    />

    <div class="flex flex-col min-h-[300px]">
      <template v-if="loading">
        <AuditoriumExceptionalitiesCardSkeleton
          v-for="n in itemsPerPage"
          :key="`skeleton-${n}`"
          :show-approval-progress="flags.enabledApprovalEngine"
        />
      </template>

      <template v-else>
        <template v-if="list?.length && canReadExceptionList">
          <AuditoriumExceptionalitiesCard
            v-for="(item, i) in list"
            :key="i"
            :exceptionId="item.exceptionId"
            :date="item?.doctorStation?.date"
            :doctor="item.doctor || { crm: {}, document: {} }"
            :scale="item?.doctorStation?.station?.schedule?.name"
            :unit="item?.doctorStation?.station?.schedule?.healthUnit?.name"
            :prejudice="item.financials.estimatedLossTotal"
            :status="item.status"
            :approval-instance="item.approvalInstance"
            @details="() => handleDetails(item)"
          />
        </template>
        <EmptyList v-else title="Nenhum pedido encontrado" />
      </template>
    </div>

    <v-pagination
      v-if="!loading && list.length > 0"
      v-model="currentPage"
      :total-visible="5"
      :length="pageCount"
      density="comfortable"
      color="primary"
      @update:model-value="changePage"
    />
  </div>

  <!-- Wiring future-proof: aqui `mode="'approvals'"`, e a seção "Registro de
       plantão" (chip + linha de vínculo de Permanência) só renderiza com
       mode === 'dashboard' — gate herdado, pré-existente à feature. Sem essa
       seção, a linha de vínculo nunca aparece e `@navigate-to-station` nunca
       dispara hoje. -->
  <StationDetailsModal
    v-if="stationSelected"
    :infos="stationSelected"
    :show-modal="isStationDetailsModalOpen"
    @update:showModal="isStationDetailsModalOpen = $event"
    @close-modal="isStationDetailsModalOpen = false"
    @edit-station="() => {}"
    @navigate-to-station="handleNavigateToStation"
    :mode="'approvals'"
    :is-pushed-externally="isRequestExceptionalitySidebarOpen || isSummaryExceptionalitySidebarOpen"
    :exceptionIdSelected="exceptionIdSelected"
    :approval-status-map="approvalStatusMap"
  />

  <RequestExceptionalitySidebar
    v-if="exceptionIdSelected"
    v-model:is-open="isRequestExceptionalitySidebarOpen"
    :exception-id="exceptionIdSelected"
    :scheduleId="stationSelected?.scheduleId || 1"
    :post-station-locked="isPostStationLocked"
    @submitSuccess="fetchData"
    @status-map-changed="handleStatusMapChange"
  />

  <SummaryExceptionalitySidebar
    v-if="exceptionIdSelected"
    :is-open="isSummaryExceptionalitySidebarOpen"
    :exception-id="exceptionIdSelected"
    :action-type="summaryActionType"
    :scheduleId="stationSelected?.scheduleId || 1"
    :post-station-locked="isPostStationLocked"
    @update:is-open="isSummaryExceptionalitySidebarOpen = $event"
    @action-completed="fetchData"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, defineAsyncComponent, watch } from 'vue'
import { useErrorToast } from '@/composables/useErrorToast'
import DsSwitch from '@/components/designSystem/Switch/index.vue'
import { useRouter, useRoute } from 'vue-router'

import AuditoriumExceptionalitiesCard from './components/AuditoriumExceptionalitiesCard.vue'
import { usePostStationLock } from '@/modules/company/PostStation/composables/usePostStationLock'
import AuditoriumExceptionalitiesCardSkeleton from '@/components/designSystem/Skeleton/AuditoriumExceptionalitiesCardSkeleton.vue'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import EmptyList from '@/components/EmptyList.vue'
import DateFilter from '@/components/Filters/DateFilter.vue'
import SelectFilter from '@/components/Filters/SelectFilter.vue'
import ValueRangeFilter, { type ValueRange } from '@/components/Filters/ValueRangeFilter.vue'
import ActiveFiltersTags, { FilterTag } from '@/components/Filters/ActiveFilters.vue'

import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useScheduleStore } from '@/pinia-store/useScheduleStore'
import { storeToRefs } from 'pinia'
import { useHealthUnitStore } from '@/pinia-store/useHealthUnitStore'
import RequestExceptionalitySidebar, {
  ApprovalStatusMap
} from '@/components/designSystem/Sidebar/RequestExceptionalitySidebar.vue'
import SummaryExceptionalitySidebar from '@/components/designSystem/Sidebar/SummaryExceptionalitySidebar.vue'
import ExceptionService from '@/Network/Services/DoctorStationException'
import StationService from '@/Network/Services/StationService'
import {
  ExceptionRequest,
  ExceptionStatusEnum,
  ExceptionActionType
} from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import type { ExceptionStatusSummaryResponse } from '@/Network/Types/Requests/DoctorStationExceptionRequests'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import ExceptionalitySummaryCard from './components/ExceptionalitySummaryCard.vue'
import { StationToUpdate } from '../../Dashboard/modals/createStation/helpers'
import StationDetailsModal from '../../Dashboard/components/stationDetailsModal.vue'
import { withNavigatedStationId } from '@/utils/permanence'

interface DateRange {
  start: Date | null
  end: Date | null
}

const router = useRouter()
const route = useRoute()
const { notifyError } = useErrorToast()
const permissionsStore = usePermissionsStore()

const canReadExceptionList = computed(() => {
  return permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.StationExceptionalitiesListReading,
    resourceType: AuthorizationResourcesAndSubjects.Schedule
  })
})

const textInput = ref('')
const loading = ref(true)
const list = ref<ExceptionRequest[]>([])

const { flags } = useFeatureFlags()
const summaryData = ref<ExceptionStatusSummaryResponse>({
  awaitingApproval: 0,
  approved: 0,
  rejected: 0
})
const summaryLoading = ref(true)

const pendingMyActionFilter = ref(false)
const openFilterName = ref<string | null>(null)
const dateFilterRef = ref<InstanceType<typeof DateFilter> | null>(null)
const valueFilterRef = ref<InstanceType<typeof ValueRangeFilter> | null>(null)
const selectedMinValue = ref<number | undefined>(undefined)
const selectedMaxValue = ref<number | undefined>(undefined)

const selectedStatusKeys = ref<string[]>([])
const selectedScheduleIds = ref<string[]>([])
const selectedHealthUnitIds = ref<string[]>([])
const selectedDateRange = ref<DateRange>({ start: null, end: null })

const approvalStatusMap = ref<ApprovalStatusMap>({
  financials: null,
  repassMode: null,
  proportionalityRule: null,
  workJourney: null
})

const selectedDateLabel = ref<string | null>(null)

const currentPage = ref(1)
const totalItems = ref(15)
const itemsPerPage = 5
const pageCount = computed(() => Math.ceil(totalItems.value / itemsPerPage))

const scheduleStore = useScheduleStore()
const { listSchedules } = storeToRefs(scheduleStore)

const healthUnitStore = useHealthUnitStore()
const { listHealthUnits } = storeToRefs(healthUnitStore)

const headerStore = useHeaderStore()

const isStationDetailsModalOpen = ref(false)
const isRequestExceptionalitySidebarOpen = ref(false)
const isSummaryExceptionalitySidebarOpen = ref(false)
const summaryActionType = ref<ExceptionActionType>(ExceptionActionType.VIEW)

const exceptionIdSelected = ref<any | null>(null)
const stationSelected = ref<StationToUpdate | null>(null)

/**
 * Trava do Pós-Plantão (RN-07 / RF-12) do plantão do pedido aberto.
 *
 * Calculada aqui, e não nas sidebars: elas são componentes de design system e
 * não podem chamar `Network.postStation`. Esta tela tem o `stationId` e a
 * `scheduleId` do plantão em `stationSelected`.
 */
const { isPostStationLocked, refresh: refreshPostStationLock } = usePostStationLock(
  () => stationSelected.value?.stationId,
  () => stationSelected.value?.scheduleId
)
const pendingExceptionId = ref<number | null>(null)

const scaleIcon = defineAsyncComponent(() => import('@/assets/icons/scale.vue'))
const statusIcon = defineAsyncComponent(() => import('@/assets/icons/status.vue'))
const hospitalIcon = defineAsyncComponent(() => import('@/assets/icons/hospital.vue'))
const moneyIcon = defineAsyncComponent(() => import('@/assets/icons/money.vue'))

const handleStatusMapChange = (statusMap: ApprovalStatusMap) => {
  approvalStatusMap.value = statusMap
}

// Navegação real da linha de vínculo de Registro de Permanência: só troca o
// `stationId` da seleção já existente — o `StationDetailsModal` (mantido
// aberto) refaz o fetch sozinho via watch interno de `props.infos.stationId`.
function handleNavigateToStation(stationId: number) {
  stationSelected.value = withNavigatedStationId(stationSelected.value, stationId)
}

const scheduleOptions = computed(() =>
  listSchedules.value.map((item) => ({
    label: item.title,
    value: String(item.value)
  }))
)

const healthUnitOptions = computed(() =>
  listHealthUnits.value.map((unit) => ({
    label: unit.title,
    value: String(unit.value)
  }))
)

const statusOptions = [
  { label: 'Em andamento', value: 'in_progress' },
  { label: 'Aprovado', value: 'approved' },
  { label: 'Recusado', value: 'rejected' },
  { label: 'Revertido', value: 'reverted' },
  { label: 'Cancelado', value: 'cancelled' }
]

const statusKeyToEnumValue: { [key: string]: ExceptionStatusEnum } = {
  in_progress: ExceptionStatusEnum.AWAITING_APPROVAL,
  approved: ExceptionStatusEnum.APPROVED,
  rejected: ExceptionStatusEnum.REJECTED,
  reverted: ExceptionStatusEnum.REVERTED,
  cancelled: ExceptionStatusEnum.CANCELED
}

const selectedStatusObjects = computed(() =>
  selectedStatusKeys.value
    .map((key) => statusOptions.find((opt) => opt.value === key))
    .filter((s): s is { label: string; value: string } => !!s)
)
const selectedScheduleObjects = computed(() =>
  selectedScheduleIds.value
    .map((id) => scheduleOptions.value.find((opt) => opt.value === id))
    .filter((s): s is { label: string; value: string } => !!s)
)
const selectedHealthUnitObjects = computed(() =>
  selectedHealthUnitIds.value
    .map((id) => healthUnitOptions.value.find((opt) => opt.value === id))
    .filter((u): u is { label: string; value: string } => !!u)
)

const hasActiveFilters = computed(() => {
  const hasSearch = textInput.value !== ''
  const hasSchedules = selectedScheduleIds.value.length > 0
  const hasUnits = selectedHealthUnitIds.value.length > 0
  const hasDate = selectedDateRange.value.start !== null || selectedDateRange.value.end !== null
  const hasStatus = selectedStatusKeys.value.length > 0
  const hasValue = selectedMinValue.value !== undefined || selectedMaxValue.value !== undefined
  return (
    hasSearch ||
    hasSchedules ||
    hasUnits ||
    hasDate ||
    hasStatus ||
    hasValue ||
    pendingMyActionFilter.value
  )
})

const unifiedFiltersList = computed<FilterTag[]>(() => {
  const list: FilterTag[] = []

  selectedStatusObjects.value.forEach((s) => {
    list.push({ id: `status-${s.value}`, label: s.label, type: 'status', value: s.value })
  })

  if (selectedDateLabel.value) {
    list.push({ id: 'date', label: selectedDateLabel.value, type: 'date' })
  }

  selectedScheduleObjects.value.forEach((s) => {
    list.push({ id: `schedule-${s.value}`, label: s.label, type: 'schedule', value: s.value })
  })

  selectedHealthUnitObjects.value.forEach((u) => {
    list.push({ id: `unit-${u.value}`, label: u.label, type: 'unit', value: u.value })
  })

  if (selectedMinValue.value !== undefined || selectedMaxValue.value !== undefined) {
    const parts: string[] = []
    if (selectedMinValue.value !== undefined) parts.push(`mín R$${selectedMinValue.value}`)
    if (selectedMaxValue.value !== undefined) parts.push(`máx R$${selectedMaxValue.value}`)
    list.push({ id: 'value', label: `Valor: ${parts.join(' – ')}`, type: 'value' })
  }

  if (pendingMyActionFilter.value) {
    list.push({ id: 'pendingMyAction', label: 'Minhas pendências', type: 'pendingMyAction' })
  }

  return list
})

const handleRemoveGenericFilter = (filter: FilterTag) => {
  switch (filter.type) {
    case 'status':
      removeStatus(filter.value)
      break
    case 'date':
      clearDate()
      break
    case 'schedule':
      removeSchedule(filter.value)
      break
    case 'unit':
      removeHealthUnit(filter.value)
      break
    case 'value':
      clearValue()
      break
    case 'pendingMyAction':
      pendingMyActionFilter.value = false
      fetchData()
      fetchSummary()
      break
  }
}

function handlePendingMyActionToggle() {
  currentPage.value = 1
  fetchData()
  fetchSummary()
}

function handleValueFilter(payload: ValueRange) {
  selectedMinValue.value = payload.min
  selectedMaxValue.value = payload.max
  openFilterName.value = null
  currentPage.value = 1
  fetchData()
  fetchSummary()
}

function clearAllFilters() {
  textInput.value = ''
  selectedScheduleIds.value = []
  selectedHealthUnitIds.value = []
  selectedDateRange.value = { start: null, end: null }
  selectedDateLabel.value = null
  selectedMinValue.value = undefined
  selectedMaxValue.value = undefined
  valueFilterRef.value?.reset()
  pendingMyActionFilter.value = false
  currentPage.value = 1
  selectedStatusKeys.value = ['in_progress']
  closeAllFilters()
  fetchData()
  fetchSummary()
}

function toggleFilter(name: string) {
  if (name !== 'date') {
    dateFilterRef.value?.close?.()
  }
  openFilterName.value = openFilterName.value === name ? null : name
}

function closeAllFilters() {
  openFilterName.value = null
  dateFilterRef.value?.close()
}

const changePage = (page: number) => {
  currentPage.value = page
  fetchData()
}

const updateUrlWithFilters = () => {
  const query: any = {}
  // `tab=` só faz sentido no shell de abas; na rota própria da fila seria um param morto.
  if (!flags.enabledApprovalsHub) query.tab = 'exceptionality'
  if (selectedStatusKeys.value.length) query.status = selectedStatusKeys.value.join(',')
  if (selectedScheduleIds.value.length) query.scheduleIds = selectedScheduleIds.value.join(',')
  if (selectedHealthUnitIds.value.length)
    query.healthUnitIds = selectedHealthUnitIds.value.join(',')
  if (selectedDateRange.value.start)
    query.startDate = selectedDateRange.value.start.toISOString().split('T')[0]
  if (selectedDateRange.value.end)
    query.endDate = selectedDateRange.value.end.toISOString().split('T')[0]
  if (textInput.value) query.search = textInput.value
  if (currentPage.value > 1) query.page = String(currentPage.value)

  router.push({ query })
}

const setFiltersFromUrl = () => {
  const query = route.query
  selectedStatusKeys.value = query.status ? String(query.status).split(',') : ['in_progress']
  selectedScheduleIds.value = query.scheduleIds ? String(query.scheduleIds).split(',') : []
  selectedHealthUnitIds.value = query.healthUnitIds ? String(query.healthUnitIds).split(',') : []
  textInput.value = query.search ? String(query.search) : ''
  currentPage.value = query.page ? Number(query.page) : 1

  if (query.startDate) {
    selectedDateRange.value.start = parseISO(String(query.startDate))
  }
  if (query.endDate) {
    selectedDateRange.value.end = parseISO(String(query.endDate))
  }

  if (query.exceptionId) {
    pendingExceptionId.value = Number(query.exceptionId)
  }

  handleDateFilter(selectedDateRange.value, false)
}

const buildFilterParams = (): Record<string, any> => {
  const params: Record<string, any> = {}

  if (selectedStatusKeys.value.length) {
    const backendStatuses = selectedStatusKeys.value.map((key) => statusKeyToEnumValue[key])
    params.status = backendStatuses.join(',')
  }

  if (selectedScheduleIds.value.length) params.scheduleIds = selectedScheduleIds.value.join(',')
  if (selectedHealthUnitIds.value.length)
    params.healthUnitIds = selectedHealthUnitIds.value.join(',')
  if (selectedDateRange.value.start) params.startDate = selectedDateRange.value.start.toISOString()
  if (selectedDateRange.value.end) params.endDate = selectedDateRange.value.end.toISOString()
  if (textInput.value) params.search = textInput.value
  if (selectedMinValue.value !== undefined) params.minValue = selectedMinValue.value
  if (selectedMaxValue.value !== undefined) params.maxValue = selectedMaxValue.value
  if (pendingMyActionFilter.value) params.pendingMyAction = true

  return params
}

const fetchData = async () => {
  loading.value = true
  const service = new ExceptionService()

  const offset = (currentPage.value - 1) * itemsPerPage
  const params = { limit: itemsPerPage, offset, ...buildFilterParams() }

  updateUrlWithFilters()

  try {
    const response = await service.list(params)
    list.value = response?.data || []
    totalItems.value = response?.count || 0
  } catch (error) {
    console.error('Erro ao listar pedidos de excepcionalidade', error)
    // Antes: swallow — a falha de load parecia "nenhum pedido".
    notifyError(error, 'Não foi possível carregar os pedidos de excepcionalidade')
    list.value = []
  } finally {
    loading.value = false
  }
}

const fetchSummary = async () => {
  if (!flags.enabledExceptionalitySummary) return

  summaryLoading.value = true
  const service = new ExceptionService()

  const filterParams = buildFilterParams()
  delete filterParams.status
  const params: Record<string, string> = Object.fromEntries(
    Object.entries(filterParams).map(([k, v]) => [k, String(v)])
  )

  try {
    summaryData.value = await service.getStatusSummary(params)
  } catch (error) {
    console.error('Erro ao buscar resumo de excepcionalidades', error)
    // Antes: swallow — os cards de resumo simplesmente não apareciam.
    notifyError(error, 'Não foi possível carregar o resumo de excepcionalidades')
  } finally {
    summaryLoading.value = false
  }
}

const onSearchByTabIndex = () => handlePendingMyActionToggle()

const buildStationForModal = (
  data: Awaited<ReturnType<StationService['getStationDetails']>>
): StationToUpdate => {
  const { doctorStation, schedule } = data
  const {
    startHour,
    endHour,
    timezone = '-03:00',
    type: stationType = 'N/A',
    ourTeam = false,
    value: stationValue,
    requestAntecipe: advanceRequested = false,
    antecipeWasPaid: advancePaid = false,
    modifiedValue = false,
    observations = [],
    userSponsor
  } = doctorStation ?? {}

  return {
    stationId: data.id,
    companyId: schedule?.healthUnit?.company?.id ?? 0,
    doctorStationId: data.id,
    date: new Date(data.date),
    startHour: new Date(startHour),
    endHour: new Date(endHour),
    timezone,
    stationType,
    ourTeam,
    value: data.value ?? 0,
    stationValue: stationValue ?? 0,
    inCash: data.inCash ?? false,
    advanceRequested,
    advancePaid,
    modifiedValue,
    position: data.position,
    checks: [],
    observations,
    healthUnitLatitude: '',
    healthUnitLongitude: '',
    doctorId: undefined,
    doctorPending: undefined,
    urlConviteDoctor: undefined,
    userSponsor,
    scheduleId: schedule?.id
  }
}

const openExceptionalitySidebar = (status: ExceptionStatusEnum) => {
  if (status === ExceptionStatusEnum.AWAITING_APPROVAL) {
    isSummaryExceptionalitySidebarOpen.value = false
    isRequestExceptionalitySidebarOpen.value = true
    return
  }
  isRequestExceptionalitySidebarOpen.value = false
  summaryActionType.value =
    status === ExceptionStatusEnum.APPROVED ? ExceptionActionType.REVERT : ExceptionActionType.VIEW
  isSummaryExceptionalitySidebarOpen.value = true
}

const handleDetails = async (item: ExceptionRequest) => {
  approvalStatusMap.value = {
    financials: null,
    repassMode: null,
    proportionalityRule: null,
    workJourney: null
  }

  try {
    const service = new StationService()
    const data = await service.getStationDetails(item.doctorStation.station.id)
    if (!data) return

    stationSelected.value = buildStationForModal(data)
    exceptionIdSelected.value = item.exceptionId
    isStationDetailsModalOpen.value = true
    openExceptionalitySidebar(item.status)

    // Depois de `stationSelected` de propósito: o gate de leitura da trava é
    // escopado na `scheduleId`, que só existe a partir do detalhe do plantão.
    await refreshPostStationLock()
  } catch (error) {
    console.error('Erro ao buscar detalhes da estação', error)
    // Antes: swallow — o clique no card não abria nada e não dizia por quê.
    notifyError(error, 'Não foi possível carregar os detalhes do plantão')
  }
}

function handleStatusSelect(value: string) {
  const currentKeys = [...selectedStatusKeys.value]
  const index = currentKeys.indexOf(value)

  if (index > -1) {
    currentKeys.splice(index, 1)
  } else {
    currentKeys.push(value)
  }
  selectedStatusKeys.value = currentKeys

  currentPage.value = 1
  fetchData()
  fetchSummary()
}

function handleScheduleSelect(value: string) {
  const currentIds = [...selectedScheduleIds.value]
  const index = currentIds.indexOf(value)

  if (index > -1) {
    currentIds.splice(index, 1)
  } else {
    currentIds.push(value)
  }
  selectedScheduleIds.value = currentIds

  currentPage.value = 1
  fetchData()
  fetchSummary()
}

function handleHealthUnitSelect(value: string) {
  const currentIds = [...selectedHealthUnitIds.value]
  const index = currentIds.indexOf(value)

  if (index > -1) {
    currentIds.splice(index, 1)
  } else {
    currentIds.push(value)
  }
  selectedHealthUnitIds.value = currentIds

  currentPage.value = 1
  fetchData()
  fetchSummary()
}

function handleDateFilter(payload: DateRange, shouldFetch = true) {
  selectedDateRange.value = payload
  if (payload.start && payload.end) {
    const formattedStart = format(payload.start, 'dd/MM/yyyy', { locale: ptBR })
    const formattedEnd = format(payload.end, 'dd/MM/yyyy', { locale: ptBR })
    if (formattedStart === formattedEnd) {
      selectedDateLabel.value = formattedStart
    } else {
      selectedDateLabel.value = `${formattedStart} - ${formattedEnd}`
    }
  } else {
    selectedDateLabel.value = null
  }

  if (shouldFetch) {
    currentPage.value = 1
    fetchData()
    fetchSummary()
  }
}

function removeStatus(value: string) {
  const index = selectedStatusKeys.value.indexOf(value)
  if (index > -1) {
    selectedStatusKeys.value.splice(index, 1)
    fetchData()
    fetchSummary()
  }
}
function removeSchedule(value: string) {
  const index = selectedScheduleIds.value.indexOf(value)
  if (index > -1) {
    selectedScheduleIds.value.splice(index, 1)
    fetchData()
    fetchSummary()
  }
}
function removeHealthUnit(value: string) {
  const index = selectedHealthUnitIds.value.indexOf(value)
  if (index > -1) {
    selectedHealthUnitIds.value.splice(index, 1)
    fetchData()
    fetchSummary()
  }
}

function clearDate() {
  selectedDateRange.value = { start: null, end: null }
  selectedDateLabel.value = null
  fetchData()
  fetchSummary()
}

function clearValue() {
  selectedMinValue.value = undefined
  selectedMaxValue.value = undefined
  valueFilterRef.value?.reset()
  fetchData()
  fetchSummary()
}

watch(isStationDetailsModalOpen, (newValue) => {
  if (!newValue) {
    isRequestExceptionalitySidebarOpen.value = false
    isSummaryExceptionalitySidebarOpen.value = false
  }
})

watch(isRequestExceptionalitySidebarOpen, (newValue) => {
  if (!newValue) {
    isStationDetailsModalOpen.value = false
  }
})

watch(isSummaryExceptionalitySidebarOpen, (newValue) => {
  if (!newValue) {
    isStationDetailsModalOpen.value = false
  }
})

watch(loading, (isLoading) => {
  if (!isLoading && pendingExceptionId.value) {
    const targetId = pendingExceptionId.value
    pendingExceptionId.value = null
    const item = list.value.find((i) => i.exceptionId === targetId)
    if (item) {
      handleDetails(item)
    }
  }
})

onBeforeUnmount(() => {
  document.removeEventListener('click', closeAllFilters)
})

onMounted(async () => {
  document.addEventListener('click', closeAllFilters)
  headerStore.setTitle(
    'Pedidos de Excepcionalidade',
    'Analise, aprove ou recuse solicitações excepcionais vinculadas a escalas e plantões'
  )

  await Promise.all([
    scheduleStore.getAllSchedules(),
    healthUnitStore.fetchAllHealthUnitsForSelect()
  ])
  setFiltersFromUrl()
  fetchData()
  fetchSummary()
})
</script>

<style scoped>
.modal-pushed-by-sidebar {
  transform: translateX(-14rem);
  transition: transform 300ms ease;
}
</style>
